import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Wallet, Menu, X, User, LogOut, Receipt, CheckSquare,
  Landmark, BarChart3, Building2, Settings, ClipboardList,
} from 'lucide-react';
import { useAuth } from '../lib/auth';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2 text-green-600 font-bold text-xl">
              <Wallet className="w-6 h-6" />
              <span>ExpenseEasy</span>
            </Link>
            <Link to="/login" className="btn-primary text-sm">Sign In</Link>
          </div>
        </div>
      </nav>
    );
  }

  const isPlatformAdmin = user.role === 'platform_admin';
  const canSubmitExpenses = !isPlatformAdmin;
  const canApprove = ['head_of_dept', 'director', 'ceo'].includes(user.role);
  const canFinance = !!user.is_finance_approver;
  const canSeeReports = ['ceo', 'finance'].includes(user.role);
  const canManageOrg = user.role === 'ceo';

  // Finance/Reports/Business Setup are scoped to a single business — a platform admin
  // has no "current business" of their own, so they reach these per-business from the
  // business detail page (Admin → Businesses → a business) rather than a global link here.
  const links = [
    canSubmitExpenses && { to: '/expenses/mine', label: 'My Expenses', icon: Receipt },
    canSubmitExpenses && { to: '/purchase-requests/mine', label: 'Purchase Requests', icon: ClipboardList },
    canApprove && { to: '/approvals', label: 'Approvals', icon: CheckSquare },
    canApprove && { to: '/purchase-request-approvals', label: 'Purchase Approvals', icon: CheckSquare },
    canFinance && { to: '/finance', label: 'Finance', icon: Landmark },
    canSeeReports && { to: '/reports', label: 'Reports', icon: BarChart3 },
    canManageOrg && { to: '/business/setup', label: 'Business Setup', icon: Settings },
    isPlatformAdmin && { to: '/admin/businesses', label: 'Businesses', icon: Building2 },
  ].filter(Boolean) as { to: string; label: string; icon: typeof Receipt }[];

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 text-green-600 font-bold text-xl">
            <Wallet className="w-6 h-6" />
            <span>ExpenseEasy</span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            {links.map(l => (
              <Link key={l.to} to={l.to} className="text-gray-600 hover:text-green-600 font-medium transition-colors flex items-center gap-1.5 text-sm">
                <l.icon className="w-4 h-4" /> {l.label}
              </Link>
            ))}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 bg-green-50 text-green-700 rounded-full px-3 py-1.5 font-medium hover:bg-green-100 transition-colors text-sm"
              >
                <User className="w-4 h-4" />
                <span className="max-w-[140px] truncate">{user.full_name || user.email}</span>
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <User className="w-4 h-4" /> Profile & Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          <button className="md:hidden p-2 text-gray-600" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-4 space-y-3">
          <div className="text-sm font-medium text-gray-500">{user.email}</div>
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className="block py-2 text-gray-700 font-medium">
              {l.label}
            </Link>
          ))}
          <Link to="/profile" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-700 font-medium">Profile & Settings</Link>
          <button onClick={handleLogout} className="block w-full text-left py-2 text-red-600 font-medium">Sign Out</button>
        </div>
      )}
    </nav>
  );
}
